import React, { useCallback, useMemo } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import type { SendingDomainResponse } from "@common/schemas/sending-domain.schema";
import { createSendingEmailSchema } from "@common/schemas/sending-email.schema";
import {
    SendingDomainMethod,
    SendingDomainStatus,
} from "@common/types/domains/emailSending";
import { useCreateSendingEmail } from "@api/sendingEmail.api";
import { useSendingDomains } from "@api/sendingDomain.api";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Input } from "@design-system/Input/Input";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { validateFormikWithZodSchema } from "@helpers/form";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { SendingEmailEntitiesField } from "./SendingEmailEntitiesField";
import { useSendingEmailEntityMapping } from "./useSendingEmailEntityMapping";
import styles from "./AddSendingEmailModal.module.scss";

const addSendingEmailFormSchema = z.object({
    sendingDomainId: z.string().min(1, "Select a domain"),
    localPart: z
        .string()
        .trim()
        .min(1, "Email is required")
        .refine((value) => !value.includes("@"), "Enter the part before @"),
    displayName: z.string(),
    isDefault: z.boolean(),
    entityIds: z.array(z.number()),
});

type AddSendingEmailFormValues = z.infer<typeof addSendingEmailFormSchema>;

const fields = createFormikFields<AddSendingEmailFormValues>();

interface AddSendingEmailModalProps {
    show: boolean;
    onClose: () => void;
}

export const AddSendingEmailModal: React.FC<AddSendingEmailModalProps> = ({
    show,
    onClose,
}) => {
    const { data: domains } = useSendingDomains();

    const verifiedDomains = (domains ?? []).filter(
        (domain) =>
            domain.status === SendingDomainStatus.verified &&
            domain.method !== SendingDomainMethod.gmail,
    );

    return (
        <PlainModal show={show} onHide={onClose}>
            {show && (
                <AddSendingEmailForm
                    domains={verifiedDomains}
                    onClose={onClose}
                />
            )}
        </PlainModal>
    );
};

interface AddSendingEmailFormProps {
    domains: SendingDomainResponse[];
    onClose: () => void;
}

const AddSendingEmailForm: React.FC<AddSendingEmailFormProps> = ({
    domains,
    onClose,
}) => {
    const createEmail = useCreateSendingEmail();
    const { toast } = useToaster();
    const { entityIdsToUuids } = useSendingEmailEntityMapping();

    const formik = useFormik<AddSendingEmailFormValues>({
        initialValues: {
            sendingDomainId: domains.length === 1 ? (domains[0]?.id ?? "") : "",
            localPart: "",
            displayName: "",
            isDefault: false,
            entityIds: [],
        },
        validate: validateFormikWithZodSchema(addSendingEmailFormSchema),
        validateOnMount: true,
        onSubmit: async (values, helpers) => {
            const domain = domains.find(
                (item) => item.id === values.sendingDomainId,
            );
            if (!domain) {
                helpers.setFieldError("sendingDomainId", "Select a domain");
                helpers.setSubmitting(false);
                return;
            }

            const entityUuids = entityIdsToUuids(values.entityIds);

            try {
                await createEmail.mutateAsync({
                    domainId: domain.id,
                    body: createSendingEmailSchema.parse({
                        email: `${values.localPart.trim()}@${domain.domain}`,
                        displayName: values.displayName.trim() || null,
                        isDefault: values.isDefault,
                        entityUuids,
                    }),
                });
                toast("Sender email added");
                onClose();
            } catch {
                helpers.setSubmitting(false);
            }
        },
    });

    const handleClose = useCallback(() => {
        formik.resetForm();
        onClose();
    }, [formik, onClose]);

    const selectedDomain = domains.find(
        (domain) => domain.id === formik.values.sendingDomainId,
    );
    const domainItems = useMemo(
        () =>
            domains.map((domain) => ({
                value: domain.id,
                label: domain.domain,
            })),
        [domains],
    );

    return (
        <FormikProvider value={formik}>
            <Form>
                <StandardModalHeader>Add sender email</StandardModalHeader>
                <VStack gap={16}>
                    <VStack gap={16}>
                        <FormikField
                            name="sendingDomainId"
                            label="Domain"
                            control="button"
                        >
                            {() => (
                                <Picker.Select
                                    items={domainItems}
                                    searchable
                                    value={selectedDomain?.id ?? null}
                                    onValueChange={(domainId) => {
                                        void formik.setFieldValue(
                                            "sendingDomainId",
                                            domainId ?? "",
                                        );
                                    }}
                                    placeholder="Select a domain"
                                    searchPlaceholder="Search domains…"
                                    emptyLabel="No verified domains"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            block
                                            size="xl"
                                        />
                                    }
                                />
                            )}
                        </FormikField>
                        <FormikField<string> name="localPart" label="Email">
                            {({ field, invalid }) => (
                                <div className={styles.localPartRow}>
                                    <Input
                                        {...field}
                                        type="text"
                                        placeholder="billing"
                                        size="xl"
                                        className={styles.localPartField}
                                        invalid={invalid}
                                        aria-required
                                    />
                                    <span className={styles.domainSuffix}>
                                        @{selectedDomain?.domain ?? "domain"}
                                    </span>
                                </div>
                            )}
                        </FormikField>
                        <fields.Text
                            name="displayName"
                            label="Display name"
                            optional
                            placeholder="Acme Billing"
                            labelTooltip="Shown as the sender name in the recipient's inbox."
                            size="xl"
                        />
                    </VStack>
                    <SendingEmailEntitiesField />
                </VStack>
                <VStack gap={8}>
                    <fields.Switch
                        name="isDefault"
                        label="Default from"
                        description="Marks this address as the workspace default from."
                    />
                    <HStack justify="end" gap={4}>
                        <Button
                            variant="secondary"
                            size="md"
                            type="button"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            type="submit"
                            hotKey="mod+enter"
                            hotKeyOptions={{
                                description: "Add sender email",
                                preventDefault: true,
                                enableOnFormTags: true,
                                scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                            }}
                            hotKeyDisplayPosition="inline"
                            loading={formik.isSubmitting}
                            disabled={!formik.isValid}
                        >
                            Add email
                        </ButtonWithLoader>
                    </HStack>
                </VStack>
            </Form>
        </FormikProvider>
    );
};
